import classNames from 'classnames';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Column, {
  ColumnBreakPoints,
} from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import Row from '@/components/utilities/row/row';
import styles from './image-media-block.module.scss';

export interface ImageMediaBlockProps {
  /** The text to show as a caption */
  caption?: string | null;
  /** The class name to apply */
  className?: string;
  /** The image to show */
  image: Content.Components['ImageMediaBlockDto']['image'];
  /** The link for the image */
  link?: Content.Components['LinkDto'] | null;
}

const ImageMediaBlock = ({
  caption,
  className,
  image,
  link,
}: ImageMediaBlockProps) => {
  const getColumnConfig = (
    size: 'small' | 'medium' | 'large' | null,
  ): { offset: ColumnBreakPoints; width: ColumnBreakPoints } => {
    switch (size) {
      case 'small':
        return {
          offset: { laptop: 5 },
          width: { laptop: 2 },
        };

      case 'medium':
        return {
          offset: { laptop: 3 },
          width: { laptop: 6 },
        };

      case 'large':
        return {
          offset: { laptop: 1 },
          width: { laptop: 10 },
        };

      default:
        return {
          offset: { laptop: 1 },
          width: { laptop: 10 },
        };
    }
  };

  const imageElement = (
    <Image
      alt={image.altText}
      aspectRatio={image.aspectRatio ?? 'threeByFour'}
      className={styles.image}
      focalPoint={image.focalPoint}
      src={image.url}
      srcSet={{
        desktop: { width: 1061 },
        laptop: { width: 782 },
        tablet: { width: 672 },
      }}
      unoptimized
      width={311}
    />
  );

  return (
    <div className={classNames(styles.component, className)}>
      <Container>
        {image.url && (
          <Row>
            <Column className={styles.column} {...getColumnConfig(image.size)}>
              {link?.url ? (
                <Link target={link.target} url={link.url}>
                  {imageElement}
                </Link>
              ) : (
                imageElement
              )}

              {caption && (
                <Typography
                  className={styles.caption}
                  tag="p"
                  tagStyle="labelMedium"
                >
                  {caption}
                </Typography>
              )}
            </Column>
          </Row>
        )}
      </Container>
    </div>
  );
};

ImageMediaBlock.displayName = 'ImageMediaBlock';

export default ImageMediaBlock;
